import {
    ButtonGradientPrimary,
    ButtonSecondary,
    ModalDialog,
    MultilineInput,
    SelectOutlined,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';

import { useCreateMeetingTemplate } from '@/api/thematic-meetings';
import { FormField } from '@/components/FormField/FormField';
import { THEMATIC_MEETING_TOPIC_LABEL_KEYS } from '@/constants/ThematicMeetingTopic';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName, type RouteNameType } from '@/navigation/types';
import {
    OBJECTIVE_MAX_LENGTH,
    TITLE_MAX_LENGTH,
} from '@/pages/ManageTemplates/MeetingPlanTemplate/validation';
import { showSuccessToast } from '@/plugins/Toast';
import { THEMATIC_MEETING_TOPICS } from '@/types/thematic-meetings';
import { toContentLanguage } from '@/utils/contentLanguage';
import { validationMessageKey } from '@/utils/validationMessage';

const TOPIC_REQUIRED_MESSAGE: TranslationKey = 'thematicMeetings.validation.topicRequired';
const TITLE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.titleRequired';
const TITLE_TOO_LONG_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.titleTooLong';
const OBJECTIVE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.objectiveRequired';
const TEXT_TOO_LONG_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.textTooLong';

/** What the server asks of a new plan, with its bounds: the rest is written in the editor. */
const newMeetingPlanSchema = z.object({
    topic: z.enum(THEMATIC_MEETING_TOPICS, { message: TOPIC_REQUIRED_MESSAGE }),
    title: z
        .string()
        .trim()
        .min(1, { message: TITLE_REQUIRED_MESSAGE })
        .max(TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
    objective: z
        .string()
        .trim()
        .min(1, { message: OBJECTIVE_REQUIRED_MESSAGE })
        .max(OBJECTIVE_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
});

type NewMeetingPlanFormData = z.infer<typeof newMeetingPlanSchema>;

/** No topic is chosen for the reader: which meeting the plan is for is the one thing they decide. */
const EMPTY_FORM = { title: '', objective: '' };

type NewMeetingPlanModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * Writes a new plan of the company: the type of meeting it is for, its name and its purpose, which
 * is all a plan needs to exist and to be listed. The plan's editor opens next, where the rest of it
 * is written.
 */
export const NewMeetingPlanModal = ({ isOpen, onClose }: NewMeetingPlanModalProps) => {
    const { t, i18n } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { mutateAsync: createTemplate, isPending: isCreating } = useCreateMeetingTemplate();

    // Written in the reader's language only, as the editor does: the server asks for a wording in at
    // least one language, and the other one is written when someone translates the plan.
    const language = toContentLanguage(i18n.language);

    const titleId = useId();
    const objectiveId = useId();

    const { control, handleSubmit, reset } = useForm<NewMeetingPlanFormData>({
        resolver: zodResolver(newMeetingPlanSchema),
        defaultValues: EMPTY_FORM,
    });

    const errorMessageOf = (message: string | undefined) =>
        message ? t(validationMessageKey(message)) : undefined;

    const close = () => {
        reset(EMPTY_FORM);
        onClose();
    };

    const topicLabel = t('templateManagement.template.meetingPlans.newPlanModal.topicLabel');
    const topicOptions = THEMATIC_MEETING_TOPICS.map((topic) => ({
        value: topic,
        label: t(THEMATIC_MEETING_TOPIC_LABEL_KEYS[topic]),
    }));

    const createPlan = async ({ topic, title, objective }: NewMeetingPlanFormData) => {
        try {
            const created = await createTemplate({
                topic,
                title: { [language]: title },
                objective: { [language]: objective },
            });

            showSuccessToast({ message: t('templateManagement.template.meetingPlans.created') });
            reset(EMPTY_FORM);
            navigate({ path: `${RouteName.MeetingPlanTemplate}/${created.id}` as RouteNameType });
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={close}
            closeAriaLabel={t('common.close')}
            title={t('templateManagement.template.meetingPlans.newPlanModal.title')}
            subtitle={t('templateManagement.template.meetingPlans.newPlanModal.subtitle')}
            headingLevel={4}
            size={'md'}
            onSubmit={handleSubmit(createPlan)}
            backdropBlur
            dataTestId={'new-meeting-plan-modal'}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary onClick={close}>{t('common.cancel')}</ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        loading={isCreating}
                    >
                        {t('templateManagement.template.meetingPlans.newPlanModal.create')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-4'}>
                <Controller
                    control={control}
                    name={'topic'}
                    render={({ field, fieldState }) => (
                        <FormField
                            label={topicLabel}
                            required
                            errorMessage={errorMessageOf(fieldState.error?.message)}
                        >
                            {/* The library's trigger takes no id for the label to point at, so it
                                is named by the label's own words. */}
                            <SelectOutlined
                                ariaLabel={topicLabel}
                                options={topicOptions}
                                // In the order the catalogue shows the topics, not the alphabet's.
                                sort={'none'}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t('common.select')}
                                error={Boolean(fieldState.error)}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    control={control}
                    name={'title'}
                    render={({ field, fieldState }) => (
                        <FormField
                            id={titleId}
                            label={t('templateManagement.meetingPlanEditor.titleLabel')}
                            required
                            errorMessage={errorMessageOf(fieldState.error?.message)}
                        >
                            <TextInput
                                id={titleId}
                                value={field.value}
                                onChange={field.onChange}
                                maxLength={TITLE_MAX_LENGTH}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    control={control}
                    name={'objective'}
                    render={({ field, fieldState }) => (
                        <FormField
                            id={objectiveId}
                            label={t('templateManagement.meetingPlanEditor.objectiveLabel')}
                            required
                            errorMessage={errorMessageOf(fieldState.error?.message)}
                        >
                            <MultilineInput
                                id={objectiveId}
                                value={field.value}
                                onChange={field.onChange}
                                rows={3}
                                maxLength={OBJECTIVE_MAX_LENGTH}
                                autoGrow
                            />
                        </FormField>
                    )}
                />
            </div>
        </ModalDialog>
    );
};
